import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import api from '../utils/api';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

function formatPeriodAr(period) {
  if (!period) return '—';
  const [y, m] = period.split('-');
  return `${MONTHS_AR[parseInt(m) - 1]} ${y}`;
}

export default function HistoryPage() {
  const { user, canManage } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadHistory(); }, []);

  async function loadHistory() {
    setLoading(true);
    try {
      // Load last 12 months of results
      const now = new Date();
      const promises = [];
      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const period = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        promises.push(
          api.get(`/commission/${user.id}/${period}`).catch(() => null)
        );
      }
      const data = await Promise.all(promises);
      setResults(data.filter(Boolean));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const totalEarned  = results.reduce((s, r) => s + parseFloat(r.total_earned || 0), 0);
  const paidCount    = results.filter(r => r.status === 'paid').length;

  return (
    <div className="page">
      <Header title="سجل العمولات" />
      <div className="page-content">

        {loading ? (
          <div className="loading-page"><div className="spinner" /><span>جاري التحميل...</span></div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="9" y1="21" x2="9" y2="9"/>
            </svg>
            <h3>لا يوجد سجل بعد</h3>
            <p style={{ fontSize: 13 }}>ستظهر هنا نتائج العمولات الشهرية</p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div style={styles.summaryCard}>
              <div style={styles.summaryItem}>
                <span style={styles.summaryNum}>{totalEarned.toLocaleString('ar', { maximumFractionDigits: 0 })}</span>
                <span style={styles.summaryLabel}>إجمالي العمولات (ر.س)</span>
              </div>
              <div style={styles.summaryDivider} />
              <div style={styles.summaryItem}>
                <span style={styles.summaryNum}>{paidCount}/{results.length}</span>
                <span style={styles.summaryLabel}>شهور مدفوعة</span>
              </div>
            </div>

            {/* Results List */}
            {results.map(r => (
              <HistoryRow key={r.period} result={r} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function HistoryRow({ result }) {
  const [expanded, setExpanded] = useState(false);
  const total = parseFloat(result.total_earned || 0);

  const statusConfig = {
    live:   { label: 'جاري',   bg: 'var(--gray-100)',  color: 'var(--gray-600)' },
    locked: { label: 'مقفل',   bg: '#dbeafe',          color: '#1e40af' },
    paid:   { label: 'مدفوع',  bg: 'var(--green-100)', color: 'var(--green-700)' },
  };
  const sc = statusConfig[result.status] || statusConfig.live;

  return (
    <div style={styles.row}>
      <div style={styles.rowHeader} onClick={() => setExpanded(!expanded)}>
        <div>
          <div style={styles.rowPeriod}>{formatPeriodAr(result.period)}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.color }}>
              {sc.label}
            </span>
            {result.override_applied && (
              <span style={{ ...styles.statusBadge, background: '#fef9c3', color: '#854d0e' }}>
                تعديل مطبق
              </span>
            )}
          </div>
        </div>
        <div style={styles.rowRight}>
          <span style={styles.rowTotal}>{total.toLocaleString('ar', { maximumFractionDigits: 0 })}</span>
          <span style={styles.rowCurrency}>ر.س</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2"
               style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s', marginRight: 4 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {expanded && (
        <div style={styles.breakdown}>
          <div className="divider" style={{ margin: '10px 0' }} />
          {[
            { label: 'الكمية',         value: result.qty_earned },
            { label: 'المرتجع الرديء', value: result.bad_return_earned },
            { label: 'التغطية',        value: result.coverage_earned },
            { label: 'التحصيل',        value: result.collection_earned },
            { label: 'الهدف الخاص',    value: result.specific_earned },
          ].map(item => (
            <div key={item.label} style={styles.breakdownRow}>
              <span style={styles.breakdownLabel}>{item.label}</span>
              <span style={styles.breakdownValue}>
                {parseFloat(item.value || 0).toLocaleString('ar', { maximumFractionDigits: 2 })} ر.س
              </span>
            </div>
          ))}
          <div className="divider" style={{ margin: '10px 0' }} />
          <div style={{ ...styles.breakdownRow }}>
            <span style={{ ...styles.breakdownLabel, fontWeight: 700, color: 'var(--gray-900)' }}>الإجمالي</span>
            <span style={{ ...styles.breakdownValue, color: 'var(--primary)', fontWeight: 800 }}>
              {total.toLocaleString('ar', { maximumFractionDigits: 2 })} ر.س
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  summaryCard: {
    background: 'linear-gradient(135deg, var(--primary-dark), var(--primary))',
    borderRadius: 16, padding: 20, marginBottom: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
    boxShadow: 'var(--shadow-green)',
  },
  summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  summaryNum: { fontSize: 24, fontWeight: 800, color: 'white' },
  summaryLabel: { fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  summaryDivider: { width: 1, height: 40, background: 'rgba(255,255,255,0.2)' },
  row: {
    background: 'white', borderRadius: 14,
    padding: '14px 16px', marginBottom: 8,
    boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)',
  },
  rowHeader: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', cursor: 'pointer',
  },
  rowPeriod: { fontSize: 16, fontWeight: 700, color: 'var(--gray-800)' },
  statusBadge: {
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
  },
  rowRight: { display: 'flex', alignItems: 'baseline', gap: 3 },
  rowTotal: { fontSize: 20, fontWeight: 800, color: 'var(--primary)' },
  rowCurrency: { fontSize: 12, color: 'var(--gray-400)' },
  breakdown: {},
  breakdownRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '4px 0',
  },
  breakdownLabel: { fontSize: 14, color: 'var(--gray-600)' },
  breakdownValue: { fontSize: 14, fontWeight: 600, color: 'var(--gray-800)' },
};
